import type { Task } from "~skillBuilder/utils/types";

import { doDont, image, title } from "../../contentBlocks";

export const TspResponsiveClassroomManagement = {
  name: "Responsive Classroom Management",
  uniqueName: "tsp-responsive-classroom-management",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Responsive Classroom Management", durationTimeText: "10 min" })}
<p>Students may test boundaries, seek connection, or express stress in unexpected ways that may be related to outside factors. A responsive approach helps you manage behavior without escalating issues, while maintaining student dignity and ensuring a safe, respectful learning environment.</p>
<div align="center">
<div style="max-width:300px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1775230642/skillbuilderAssets/optimized/Teacher_with_three_students_standing_thumbs_up.jpg", alt: "Illustration of a teacher with three students standing and giving thumbs up." })}
</div>
</div>
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/redirection",
          label: "Redirection versus Reactive",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Reacting in an abrasive manner or harshly to unwanted behaviors can escalate students further, and do the opposite of maintaining a student-centered approach.</p>
<div style="overflow-x: auto; margin-top: 12px;">
  <table style="width:100%; border-collapse: collapse; font-size: 14px;">
    <thead>
      <tr>
        <th style="text-align:left; padding:8px; border-bottom:1px solid #ccc;">Redirection</th>
        <th style="text-align:left; padding:8px; border-bottom:1px solid #ccc;">Reactive</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="padding:8px; vertical-align:top;">Calmly guiding the student back to task or behavior expectations.</td>
        <td style="padding:8px; vertical-align:top;">Responding emotionally or punitively to a behavior.</td>
      </tr>
      <tr>
        <td style="padding:8px; vertical-align:top;">Working with the student on overcoming obstacles.</td>
        <td style="padding:8px; vertical-align:top;">Immediately shutting a student down and writing them off.</td>
      </tr>
      <tr>
        <td style="padding:8px; vertical-align:top;">&ldquo;Let&rsquo;s take a breath and try that again.&rdquo;</td>
        <td style="padding:8px; vertical-align:top;">&ldquo;You know better than to act that way.&rdquo;</td>
      </tr>
      <tr>
        <td style="padding:8px; vertical-align:top;">Focuses on the why behind the behavior and offers support.</td>
        <td style="padding:8px; vertical-align:top;">Blames the student and focuses on the behavior itself.</td>
      </tr>
      <tr>
        <td style="padding:8px; vertical-align:top;">Uses non-verbal cues, gentle reminders, or proximity to re-engage.</td>
        <td style="padding:8px; vertical-align:top;">Raises voice, uses sarcasm, or shames in front of peers.</td>
      </tr>
      <tr>
        <td style="padding:8px; vertical-align:top;">Wants to help the student regulate their emotions and de-escalate their frustration.</td>
        <td style="padding:8px; vertical-align:top;">Does not assist in regulating emotions, is fine for the student to stay escalated.</td>
      </tr>
    </tbody>
  </table>
</div>
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/behavior-communication",
          label: "Behavior can be a Form of Communication",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Behavior, especially unwanted behavior, is indicative of the student experiencing something larger. It can reflect confusion, fear, frustration, or unmet needs.</p>
<p>While it may not be feasible to decipher every student behavior you encounter, a good rule of thumb is to pause before you react. Ask yourself, &ldquo;What might this student need?&rdquo;</p>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/expectations",
          label: "Setting Clear, Fair Expectations",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
  <li>Review the classroom and school expectations.</li>
  <li>Revisit them often throughout your interactions with the students.</li>
  <li>Be consistent in your approach.</li>
</ul>
<p>Frame expectations positively: &ldquo;Eyes on the speaker so you don&rsquo;t miss anything important!&rdquo; vs &ldquo;Pay attention or you&rsquo;ll get in trouble.&rdquo;</p>
`,
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/restorative",
          label: "Restorative Approaches to Conflict",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Punishment might stop a behavior, but it doesn&rsquo;t teach repair or help teach accountability.</p>
<ul style="margin-left: 16px;">
  <li>❌ Avoid punishments that exclude or embarrass (ex: standing in the corner, facing the wall, moving a student&apos;s desk to isolation, etc).</li>
  <li>❌ Don&rsquo;t write a student off immediately if they&rsquo;ve acted out.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/power-struggles",
          label: "Prevent Power Struggles",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>The most common trap educators can fall into is engaging in a &ldquo;power struggle&rdquo; or a &ldquo;power battle&rdquo; with a student.</p>
${doDont({
  doText:
    "Repeat your direction calmly and consistently; offer choices to promote student agency; pick your battles.",
  dontText:
    "Engage in back and forth arguments; show frustration; single out or humiliate students in front of others.",
})}
`,
          },
        },
      ],
    },
    // Step 7
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 7.1
        {
          attribute: "the-sub-playbook.responsive-classroom-management/practice-action",
          label: "Practice in Action",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>Situation:</strong></p>
<p>The day is winding down in a 5th-grade classroom. You&rsquo;ve given a clear, positive expectation: &ldquo;Let&rsquo;s get our desks cleared so we can head to recess on time!&rdquo; Most students comply, but &ldquo;Jackson&rdquo; sits with his arms crossed, muttering, &ldquo;I&apos;m not doing it. You can&apos;t make me.&rdquo;</p>
<p>Before speaking, you take a breath. Instead of thinking &ldquo;Jackson is being disrespectful,&rdquo; you ask yourself: &ldquo;What might he need?&rdquo; It&rsquo;s the end of the day; he might be overstimulated, frustrated, or just tired.</p>
<p><strong>Potential Solution:</strong></p>
<p>You offer choice and agency. You address the student calmly without drawing the attention of the rest of the classroom by saying, &ldquo;Jackson, you can clear the desk yourself in the next minute, or I can help you put the first two books away so we can both get outside for some fresh air. Which would be more helpful?&rdquo;</p>
<p>You avoid the power struggle. If he grumbles, you don&apos;t engage. You calmly repeat the direction and give him &ldquo;take-up time&rdquo; by walking to another part of the room.</p>
<p>Once Jackson finally starts moving, you don&apos;t treat him any differently. You give him a quick thumbs-up or a quiet, &ldquo;I appreciate you getting that done, Jackson. Let&apos;s have a great recess.&rdquo; This keeps the connection and relationship on track to end the day positively.</p>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
